import { Link } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useMidiStore } from '@/stores/midiStore'
import { useWebMidi } from '@/hooks/useWebMidi'
import { HomeWidget } from '../HomeWidget'

/**
 * At-a-glance MIDI readout for the home dashboard. Self-hides when
 * the browser doesn't support Web MIDI at all; shows a prompt to
 * open the router when supported but no hardware is connected yet.
 */
export function MidiStatus() {
  const supported = useMidiStore(s => s.supported)
  const hwInputs = useMidiStore(s => s.hwInputs)
  const hwOutputs = useMidiStore(s => s.hwOutputs)
  const routes = useMidiStore(s => s.routes)
  const { requestAccess } = useWebMidi()

  // Nudge Web MIDI access so the widget reflects reality as soon as
  // the user lands on /home. If the user has already granted access
  // in this origin this is a no-op; otherwise the browser will
  // surface its own permission prompt on first load.
  useEffect(() => {
    requestAccess()
  }, [requestAccess])

  if (!supported) return null

  const inputCount = hwInputs.length
  const outputCount = hwOutputs.length
  const routeCount = routes.length

  return (
    <HomeWidget
      title="MIDI status"
      action={
        <Link
          to="/werkshop/midi"
          className="text-[10px] font-semibold uppercase tracking-[0.14em] text-accent hover:text-[color:var(--accent-hover)]"
        >
          Open →
        </Link>
      }
    >
      {/* Wrap the whole body in the same Link as the "Open →" so
          any tap on the card opens the router, matching
          LatestLayout / LatestProject. Hover hints with the same
          surface-sunken bg the other two tiles use. */}
      <Link
        to="/werkshop/midi"
        className="block space-y-1.5 text-sm text-content-primary py-1.5 rounded-md hover:bg-surface-sunken px-2 -mx-2 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-second" />
          <span>
            {inputCount} {inputCount === 1 ? 'input' : 'inputs'} · {outputCount}{' '}
            {outputCount === 1 ? 'output' : 'outputs'}
          </span>
        </div>
        <div className="text-xs text-content-secondary">
          {routeCount} active {routeCount === 1 ? 'route' : 'routes'}
        </div>
      </Link>
    </HomeWidget>
  )
}
